/**
 * 登录后回跳目标的收敛。
 *
 * 会话过期 / 401 时，前端把「当前地址」塞进 `?redirect=` 带去登录页，登录成功后原样跳回。
 * 问题是当前地址可能带着**一次性标记**——尤其是 `?blocked=`：那是「门禁把你拦回概览」的信号，
 * 概览页看到它就弹一次拦截弹窗。它一旦被腌进 redirect，就会在每次会话过期重登后复发：
 *
 *   401 → /login?redirect=/?blocked=1 → 登录成功 → 跳回 /?blocked=1 → 概览又弹一次
 *
 * dev 2026-09-09 pony.ai 就是这样：一个 NORMAL、已开通 MT5、七条接口全 200 的正常客户，
 * 每次登录都被告知「很抱歉，您暂不符合我们的开户条件」，而地址栏随后被清干净、
 * 接口面板全绿，查不到任何拒绝——因为根本没有接口拒绝，是这个残留参数在自弹自演。
 *
 * 所以回跳目标一律过这里：剥掉一次性标记，并且只接受站内路径。
 */

/** 一次性门禁标记：只服务于「拦回概览后弹一次」，绝不能被带进 redirect。 */
const TRANSIENT_QUERY_KEYS = ['blocked']

/** URL 构造器要一个 base 才能解析相对路径；这个域名不会被真的请求。 */
const DUMMY_ORIGIN = 'http://portal.invalid'

/**
 * 把一个 fullPath 收敛成可安全回跳的站内目标。
 *
 * 返回 `null` 表示「不必带 redirect」——目标就是首页，或者这个地址不可信
 * （`//evil.com`、`https://…` 这类协议相对与绝对地址一律拒掉，免得 redirect 变成开放跳转）。
 */
export function redirectTargetOf(fullPath: string | null | undefined): string | null {
  if (typeof fullPath !== 'string' || !fullPath.startsWith('/') || fullPath.startsWith('//')) {
    return null
  }
  const url = new URL(fullPath, DUMMY_ORIGIN)
  for (const key of TRANSIENT_QUERY_KEYS) {
    url.searchParams.delete(key)
  }
  const query = url.searchParams.toString()
  const target = `${url.pathname}${query ? `?${query}` : ''}${url.hash}`
  return target === '/' ? null : target
}

/** 跳登录页时用的 query：目标是首页或不可信就不带 redirect。 */
export function redirectQueryOf(fullPath: string | null | undefined): { redirect?: string } {
  const target = redirectTargetOf(fullPath)
  return target ? { redirect: target } : {}
}
